import { useCallback, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fg } from '@atlaskit/platform-feature-flags';
import { useOnceWhen } from '@atlassian/teams-common/use-once';

import { useCreateTeamService } from '../../hooks/use-create-team-service';
import { type TeamMembershipSettings } from '../../types';
import { type InvitedUser } from '../../types/user';

import { type UseTeamCreateMutation } from './types';
import useTeamCreateConfiguration from './useTeamCreateConfiguration';

function useTeamCreateMutation(props: UseTeamCreateMutation): {
	isFetching: boolean;
	createTeamAndAddMembers: (
		teamName: string,
		requestedMembers: InvitedUser[],
		membershipSettings: TeamMembershipSettings,
		typeId?: string,
		suggestedMembersCount?: number,
	) => void;
} {
	const { onCreateTeamSuccess, onCreateTeamFailed } = useTeamCreateConfiguration(props);

	const { cloudId, orgId, product } = props;

	const [selectedMembers, setSelectedMembers] = useState<InvitedUser[]>([]);
	const [numberOfSuggestedMembers, setNumberOfSuggestedMembers] = useState<number>(0);
	// need a state to keep sync status of 2 requests: Create team and Invite members
	const [isFetching, setIsFetching] = useState(false);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const {
		loading: isCreatingTeam,
		error: errorCreateTeam,
		data: teamData,
		createTeam,
	} = useCreateTeamService(cloudId, product, undefined, orgId);

	// This function is used to create teams
	const handleCreateTeamWithMembers = useCallback(
		(
			teamName: string,
			requestedMembers: InvitedUser[],
			membershipSettings: TeamMembershipSettings,
			typeId?: string,
			suggestedMembersCount?: number,
		): void => {
			if (isCreatingTeam) {
				return;
			}
			setSelectedMembers(requestedMembers);
			setNumberOfSuggestedMembers(suggestedMembersCount ?? 0);
			setIsFetching(true);
			const memberIds = requestedMembers.map((member) => member.id);
			return createTeam(teamName, membershipSettings, memberIds, typeId);
		},
		[createTeam, isCreatingTeam],
	);

	const isFinishedCreateTeam = !errorCreateTeam && !isCreatingTeam && teamData;

	// call callbacks for creating teams
	useOnceWhen(
		!!(isFinishedCreateTeam || errorCreateTeam),
		async () => {
			// always call onCreateTeamSuccess when creating team successfully, though inviting members can fail
			// since users can work around by re-inviting members in the team profile page.
			if (isFinishedCreateTeam) {
				const successfullyInvitedMembers = fg('handle_partial_create_team_failures')
					? selectedMembers.filter((m) => teamData.memberIds?.includes(m.id))
					: selectedMembers;
				const failedInvitedMembers = fg('handle_partial_create_team_failures')
					? selectedMembers.filter((m) => !teamData.memberIds?.includes(m.id))
					: undefined;

				await onCreateTeamSuccess(
					teamData!,
					successfullyInvitedMembers,
					failedInvitedMembers,
					numberOfSuggestedMembers,
				);
				setIsFetching(false);
			}

			if (errorCreateTeam) {
				onCreateTeamFailed(errorCreateTeam, selectedMembers);
				// no need to invite anyone to team as members already invited
				setIsFetching(false);
			}
		},
		[
			errorCreateTeam,
			isFinishedCreateTeam,
			onCreateTeamFailed,
			onCreateTeamSuccess,
			selectedMembers,
			numberOfSuggestedMembers,
			teamData,
			setIsFetching,
			createAnalyticsEvent,
			product,
		],
	);

	return { isFetching, createTeamAndAddMembers: handleCreateTeamWithMembers };
}

export default useTeamCreateMutation;
